import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
} from 'chart.js';
import { Line, Scatter, Bar } from 'react-chartjs-2';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

const CompanyPerformance = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get('https://rotica.in/get_company_performance.php');
                if (response.data.success) {
                    setData(response.data);
                } else {
                    setError("Failed to load data");
                }
            } catch (err) {
                console.error(err);
                setError("Network Error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <div className="loading-container">Loading Performance Metrics...</div>;
    if (error) return <div className="error-container">{error}</div>;
    if (!data) return null;

    // 1. Subscription Patterns (Line Chart)
    const subscriptionData = {
        labels: data.subscription_patterns.starts.map(d => d.start_date),
        datasets: [
            {
                label: 'New Subscriptions',
                data: data.subscription_patterns.starts.map(d => d.count),
                borderColor: '#2ecc71',
                backgroundColor: 'rgba(46, 204, 113, 0.5)',
                tension: 0.3
            },
            {
                label: 'Ending Subscriptions',
                data: data.subscription_patterns.ends.map(d => d.count),
                borderColor: '#e74c3c',
                backgroundColor: 'rgba(231, 76, 60, 0.5)',
                tension: 0.3
            }
        ]
    };

    // 2. Revenue (Line Chart)
    const revenueData = {
        labels: data.revenue_data.d2c.map(d => d.date),
        datasets: [
            {
                label: 'D2C Revenue (Est.)',
                data: data.revenue_data.d2c.map(d => d.amount),
                borderColor: '#3498db',
                tension: 0.4
            },
            {
                label: 'B2B Revenue (Est.)',
                data: data.revenue_data.b2b.map(d => d.amount),
                borderColor: '#9b59b6',
                tension: 0.4
            }
        ]
    };

    // 3. Delays (Bar Chart)
    const delayData = {
        labels: data.delay_patterns.map(d => d.date),
        datasets: [
            {
                label: 'Total Delay Days',
                data: data.delay_patterns.map(d => d.total_delay_days),
                backgroundColor: '#e67e22'
            }
        ]
    };

    // 4. Retention (Bar Histogram)
    const retentionChartData = {
        labels: data.retention_patterns.map(d => `${d.order_count} Orders`),
        datasets: [{
            label: 'User Count',
            data: data.retention_patterns.map(d => d.user_count),
            backgroundColor: '#1abc9c'
        }]
    };

    // 5. Cancellations (Scatter)
    // Map dates to numbers for scatter x-axis or use category
    const cancellationPoints = data.cancellation_patterns.map(d => ({
        x: d.date,
        y: parseInt(d.count) || 0, // count as Y
        r: (parseInt(d.count) || 0) * 2 // radius size based on count
    }));

    // For scatter with date, strict parsing is needed or use labels. 
    // Simplified: Just showing daily counts as Line/Bar is often better for "patterns". 
    // But user asked for Scatter. Let's try to map it roughly.
    const cancellationData = {
        labels: data.cancellation_patterns.map(d => d.date),
        datasets: [{
            type: 'bar', // Fallback to bar for clearer daily visibility
            label: 'Cancellations',
            data: data.cancellation_patterns.map(d => d.count),
            backgroundColor: '#c0392b'
        }]
    };

    return (
        <div className="dashboard-container">
            <div className="main-content">
                <header className="page-header">
                    <h1>Company Performance</h1>
                    <p className="subtitle">Real-time business analytics</p>
                </header>

                <div className="stats-cards">
                    <div className="stat-card">
                        <h3>Avg Subscription Qty</h3>
                        <p>{data.avg_subscription_qty}</p>
                    </div>
                </div>

                <div className="charts-grid-performance">
                    {/* Row 1 */}
                    <div className="chart-card">
                        <h3>Subscription Patterns</h3>
                        <div className="chart-wrapper">
                            <Line data={subscriptionData} />
                        </div>
                    </div>
                    <div className="chart-card">
                        <h3>Revenue Trends (B2B vs D2C)</h3>
                        <div className="chart-wrapper">
                            <Line data={revenueData} />
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="chart-card">
                        <h3>Cancellation Patterns</h3>
                        <p style={{ fontSize: '12px', color: '#666' }}>Daily cancellation counts</p>
                        <div className="chart-wrapper">
                            <Bar data={cancellationData} />
                        </div>
                    </div>
                    <div className="chart-card">
                        <h3>Delay Impact</h3>
                        <div className="chart-wrapper">
                            <Bar data={delayData} />
                        </div>
                    </div>

                    {/* Row 3 - Retention */}
                    <div className="chart-card full-width">
                        <h3>Customer Retention (Orders per User)</h3>
                        <div className="chart-wrapper">
                            <Bar data={retentionChartData} />
                        </div>
                    </div>

                    {/* Row 4 - Entity Locations */}
                    <div className="chart-card">
                        <h3>Manufacturer Locations</h3>
                        <p style={{ fontSize: '12px', color: '#666' }}>
                            Registered Manufacturers
                        </p>
                        <div className="map-wrapper">
                            <MapContainer center={[19.60, 75.55]} zoom={6} style={{ height: '100%', width: '100%' }}>
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; OpenStreetMap'
                                />
                                {data.entity_locations?.manufacturers?.map((pos, idx) => (
                                    <CircleMarker key={`loc-m-${idx}`} center={[pos.lat, pos.lng]} radius={10} color="#c0392b" fillColor="#e74c3c" fillOpacity={0.8}>
                                        <Popup>Manufacturer: {pos.name}</Popup>
                                    </CircleMarker>
                                ))}
                            </MapContainer>
                        </div>
                    </div>
                    <div className="chart-card">
                        <h3>Distributor Locations</h3>
                        <p style={{ fontSize: '12px', color: '#666' }}>
                            Registered Distributors
                        </p>
                        <div className="map-wrapper">
                            <MapContainer center={[19.60, 75.55]} zoom={6} style={{ height: '100%', width: '100%' }}>
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; OpenStreetMap'
                                />
                                {data.entity_locations?.distributors?.map((pos, idx) => (
                                    <CircleMarker key={`loc-d-${idx}`} center={[pos.lat, pos.lng]} radius={10} color="#8e44ad" fillColor="#9b59b6" fillOpacity={0.8}>
                                        <Popup>Distributor: {pos.name}</Popup>
                                    </CircleMarker>
                                ))}
                            </MapContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyPerformance;
